import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, ArrowRight, Loader2, Building2, Lock } from "lucide-react";
import { SocietyStateBadge, useForm } from "@/components/developer/shared";

function SocietyDialog({ soc, onClose, onSaved }) {
  const { form, bind } = useForm(soc ? { ...soc, towers: (soc.towers || []).join(", ") } : { name: "", code: "", city: "", address: "", towers: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (soc) await api.put(`/developer/societies/${soc.id}`, { name: form.name, city: form.city, address: form.address, towers: form.towers });
      else await api.post("/developer/societies", form);
      toast.success(soc ? "Society updated" : "Society added");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const fields = [["name", "Society name"], ...(soc ? [] : [["code", "Code"]]), ["city", "City"], ["address", "Address"], ["towers", "Towers (comma separated)"]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dev-society-dialog">
        <DialogHeader><DialogTitle>{soc ? "Edit society" : "Add society"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, label]) => (
            <div key={k}><Label className="text-xs">{label}</Label><Input className="mt-1" data-testid={`dev-society-${k}-input`} {...bind(k)} /></div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="dev-society-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="dev-society-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HandoverDialog({ soc, onClose, onSaved }) {
  const { form, bind } = useForm({ name: "", email: soc.handover_email || "", password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/developer/societies/${soc.id}/handover`, form);
      toast.success("Handover login created — share it with the committee");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dev-handover-dialog">
        <DialogHeader>
          <DialogTitle>Hand over {soc.name}</DialogTitle>
          <DialogDescription>Create the committee's login. They must change the password at first sign-in; after that the society is locked for you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Committee contact name</Label><Input className="mt-1" data-testid="dev-handover-name-input" {...bind("name")} /></div>
          <div><Label className="text-xs">Login email</Label><Input className="mt-1" type="email" data-testid="dev-handover-email-input" {...bind("email")} /></div>
          <div><Label className="text-xs">Temporary password</Label><Input className="mt-1" type="text" data-testid="dev-handover-password-input" placeholder="min 6 characters" {...bind("password")} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="dev-handover-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="dev-handover-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create handover login</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SocietyCard({ s, onManage, onEdit, onHandover, onDelete }) {
  const locked = s.state === "handed_over";
  return (
    <div className={`rounded-xl border border-border p-5 ${locked ? "bg-zinc-100 opacity-60 grayscale" : "bg-card"}`} data-testid={`dev-society-card-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-1.5 font-cabinet text-lg font-bold text-brand"><Building2 className="h-4 w-4 text-brand-green" />{s.name}</h3>
          <p className="text-xs text-zinc-500">{s.code} · {s.city || "—"} · {s.ticket_count} tickets · {s.resident_count} residents</p>
          {s.state === "handover_pending" && <p className="mt-1 text-xs text-sky-700">Waiting for {s.handover_email} to sign in and change the password.</p>}
        </div>
        <SocietyStateBadge s={s} testid={`dev-society-state-${s.id}`} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {locked ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-zinc-500" data-testid={`dev-society-locked-${s.id}`}><Lock className="h-3.5 w-3.5" />Handed over to the committee — no longer managed by you</span>
        ) : (
          <>
            <Button size="sm" onClick={onManage} data-testid={`dev-society-manage-${s.id}`}>Manage<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid={`dev-society-edit-${s.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
            <Button size="sm" variant="outline" onClick={onHandover} data-testid={`dev-society-handover-${s.id}`}><KeyRound className="mr-1 h-3.5 w-3.5" />{s.state === "handover_pending" ? "Update handover login" : "Hand over"}</Button>
            {s.state === "with_developer" && <Button size="sm" variant="ghost" className="text-red-600" onClick={onDelete} data-testid={`dev-society-delete-${s.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>}
          </>
        )}
      </div>
    </div>
  );
}

export default function DevSocieties() {
  const { scopeInto } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [handover, setHandover] = useState(null);
  const load = () => api.get("/developer/societies").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This can't be undone.`)) return;
    try { await api.delete(`/developer/societies/${s.id}`); toast.success("Society deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const manage = (s) => { scopeInto(s.id); navigate("/dashboard"); };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="My Societies" subtitle="Developer Portal" description="Set up your societies, manage them, and hand them over to their committees.">
        <Button onClick={() => setEditing("new")} data-testid="dev-add-society-btn"><Plus className="mr-1.5 h-4 w-4" />Add Society</Button>
      </PageHeader>
      {list === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-zinc-400" />
        : list.length === 0 ? <p className="text-sm text-zinc-500" data-testid="dev-societies-empty">No societies yet. Add your first one.</p>
        : <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">{list.map((s) => (
          <SocietyCard key={s.id} s={s} onManage={() => manage(s)} onEdit={() => setEditing(s)} onHandover={() => setHandover(s)} onDelete={() => del(s)} />
        ))}</div>}
      {editing && <SocietyDialog soc={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {handover && <HandoverDialog soc={handover} onClose={() => setHandover(null)} onSaved={() => { setHandover(null); load(); }} />}
    </div>
  );
}
